import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, TextInput } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus } from '../components/labels';
import type { Command } from './Commands';

type Nhip = { giayDatCuoc?: number; giayTraThuong?: number; lechGiay?: number };
type Full = Required<Nhip>;
type Row = { id: string; name: string; effective: Full; defaults: Full; override: Nhip | null; stopSeconds: number | null };
type Pending = { id: string; status: string; createdAt: string };
type Data = { games: Row[]; limits: Record<keyof Nhip, [number, number]>; meta: { version: number; source: string; at: string | null }; pending: Pending[] };
// one input per key; '' = the file default
type Draft = { dat: string; tra: string; lech: string };

const KEYS: [keyof Draft, keyof Nhip][] = [['dat', 'giayDatCuoc'], ['tra', 'giayTraThuong'], ['lech', 'lechGiay']];
const toDraft = (n: Nhip | null): Draft => ({
  dat: n?.giayDatCuoc != null ? String(n.giayDatCuoc) : '', tra: n?.giayTraThuong != null ? String(n.giayTraThuong) : '', lech: n?.lechGiay != null ? String(n.lechGiay) : '',
});
const same = (a: Draft, b: Draft) => a.dat === b.dat && a.tra === b.tra && a.lech === b.lech;
const empty = (d: Draft) => !d.dat && !d.tra && !d.lech;
// null = every key back to the file default
const toNhip = (d: Draft): Nhip | null => {
  const n: Nhip = {};
  for (const [k, key] of KEYS) if (d[k] !== '') n[key] = Number(d[k]);
  return Object.keys(n).length ? n : null;
};
const digits = (s: string) => s.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 4);
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

/** What a draft means for one game: values in force once saved (draft over the file default). */
const resulting = (g: Row, d: Draft): Full => ({
  giayDatCuoc: d.dat !== '' ? Number(d.dat) : g.defaults.giayDatCuoc,
  giayTraThuong: d.tra !== '' ? Number(d.tra) : g.defaults.giayTraThuong,
  lechGiay: d.lech !== '' ? Number(d.lech) : g.defaults.lechGiay,
});

function problem(g: Row, d: Draft, limits: Data['limits']): string | null {
  const r = resulting(g, d);
  for (const [, key] of KEYS) {
    const [min, max] = limits[key];
    if (r[key] < min || r[key] > max) return `${LABEL[key]} phải từ ${min} đến ${max}`;
  }
  if (g.stopSeconds != null && r.giayDatCuoc <= g.stopSeconds) return `Giây đặt cược phải lớn hơn ${g.stopSeconds} (lúc ngừng nhận cược)`;
  return null;
}
const LABEL: Record<keyof Nhip, string> = { giayDatCuoc: 'Giây đặt cược', giayTraThuong: 'Giây trả thưởng', lechGiay: 'Lệch pha' };
const line = (f: Full) => `${f.giayDatCuoc} giây cược + ${f.giayTraThuong} giây trả thưởng${f.lechGiay ? `, lệch ${f.lechGiay} giây` : ''}`;

export function GameTimingPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['game-timing'], queryFn: () => api.get<Data>('/api/admin/games/nhip') });
  // id -> draft for rows that differ from what the server holds
  const [draft, setDraft] = useState<Map<string, Draft>>(new Map());
  // version the draft was built on, taken at the first edit (see GameLimits)
  const [base, setBase] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/games/nhip', {
      changes: Object.fromEntries([...draft].map(([id, d]) => [id, toNhip(d)])),
      expect: base ?? q.data!.meta.version,
    }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
    onError: (e) => { if (isStale(e)) setSaving(false); },
  });
  const stale = isStale(m.error);
  const discard = () => { setDraft(new Map()); setBase(null); m.reset(); qc.invalidateQueries({ queryKey: ['game-timing'] }); };

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={6} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { games, limits, meta, pending } = q.data;
  const byId = new Map(games.map((g) => [g.id, g]));
  const editable = can('games.config');
  const cur = (g: Row) => draft.get(g.id) ?? toDraft(g.override);
  const edit = (g: Row, patch: Partial<Draft>) => {
    const next = { ...cur(g), ...patch };
    const n = new Map(draft);
    if (same(next, toDraft(g.override))) n.delete(g.id); else n.set(g.id, next);
    setDraft(n);
    setBase(n.size ? (base ?? meta.version) : null);
  };
  const bad = [...draft].map(([id, d]) => problem(byId.get(id)!, d, limits)).find(Boolean) ?? null;

  return (
    <div className="page page--with-bar">
      <PageHeader title="Nhịp Tài Xỉu"
        lead="Mỗi bàn Tài Xỉu chạy đồng hồ riêng: số giây nhận cược, số giây trả thưởng và độ lệch pha. Ô để trống = dùng mặc định. Lưu xong, ván đang chạy giữ nguyên độ dài; ván kế tiếp theo độ dài mới (không cần khởi động lại, không mở ván khi ván trước chưa trả thưởng xong)." />
      <div className="row health">
        <span>Cấu hình: {meta.source === 'default' ? <span className="muted">chưa đặt từ CMS</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
      </div>
      {pending.length ? (
        <>
          <InlineNotification kind="info" lowContrast hideCloseButton title={`${pending.length} lệnh đang chờ`}
            subtitle="Đợi lệnh đang gửi xong rồi mới tạo lệnh mới: lệnh tạo trên phiên bản cũ sẽ bị hệ A từ chối." />
          <ul className="list pending-list">
            {pending.map((p) => (
              <li key={p.id}><CommandStatus s={p.status} /><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span></li>
            ))}
          </ul>
        </>
      ) : null}
      {stale ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton
          title="Cài đặt vừa được người khác đổi"
          subtitle={`Bản bạn đang sửa dựa trên phiên bản ${base ?? '?'}, trên máy chủ đã là phiên bản ${meta.version}. Chưa lưu gì. Bỏ bản nháp và tải lại rồi sửa lại.`}
          actionButtonLabel="Bỏ bản nháp và tải lại" onActionButtonClick={discard} />
      ) : null}
      <table className="plain vis">
        <thead>
          <tr><th>Game</th><th>Mặc định</th><th>Giây đặt cược</th><th>Giây trả thưởng</th><th>Lệch pha (giây)</th><th>Đang áp dụng</th></tr>
        </thead>
        <tbody>
          {games.map((g) => {
            const d = cur(g);
            const err = draft.has(g.id) ? problem(g, d, limits) : null;
            return (
              <tr key={g.id} className={draft.has(g.id) ? 'is-changed' : undefined}>
                <td className="is-title"><div className="vis__name">{g.name}</div><div className="muted num">{g.id}</div></td>
                <td data-label="Mặc định" className="muted">{line(g.defaults)}</td>
                {KEYS.map(([k, key]) => (
                  <td key={k} data-label={LABEL[key]}>
                    <TextInput id={`nhip-${g.id}-${k}`} size="sm" hideLabel labelText={`${LABEL[key]} ${g.name}`} disabled={!editable}
                      inputMode="numeric" placeholder={String(g.defaults[key])} value={d[k]}
                      onChange={(e) => edit(g, { [k]: digits(e.target.value) } as Partial<Draft>)} />
                    <div className="muted">{limits[key][0]} đến {limits[key][1]}</div>
                  </td>
                ))}
                <td data-label="Đang áp dụng">
                  <div>{line(g.effective)}</div>
                  {editable && !empty(d) ? <Button kind="ghost" size="sm" onClick={() => edit(g, { dat: '', tra: '', lech: '' })}>Về mặc định</Button> : null}
                  {err ? <div className="muted" role="alert">{err}</div> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="note">Tài Xỉu MD5 ngừng nhận cược vài giây trước giờ đóng nên số giây đặt cược của MD5 phải lớn hơn số giây đó. Đổi lệch pha dịch mốc mở ván của bàn đó; hai bàn đã chạy theo hai đồng hồ riêng nên không cần lệch để chơi cùng lúc. Ngay sau khi đổi độ dài, ván kế tiếp mở ở mốc kế tiếp của đồng hồ mới, nên bàn đó có thể nghỉ thêm tối đa một chu kỳ mới một lần.</p>
      {draft.size ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{draft.size}</span> game đã đổi, chưa lưu{bad ? <span className="muted">: {bad}</span> : null}</span>
          <Button kind="ghost" size="md" onClick={discard}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={stale || !!bad} onClick={() => setSaving(true)}>Lưu, áp dụng từ ván sau</Button>
        </div>
      ) : null}
      <Modal open={saving} modalHeading={`Đổi nhịp ${draft.size} game`} primaryButtonText="Áp dụng từ ván sau" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setSaving(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <table className="plain">
            <thead><tr><th>Game</th><th>Trước</th><th>Sau</th></tr></thead>
            <tbody>
              {[...draft].map(([id, d]) => {
                const g = byId.get(id)!;
                return <tr key={id}><td className="is-title">{g.name}</td><td data-label="Trước">{line(g.effective)}</td><td data-label="Sau">{line(resulting(g, d))}</td></tr>;
              })}
            </tbody>
          </table>
          <p>Hệ A áp dụng ngay, không chờ người duyệt. Ván đang chạy giữ độ dài cũ; ván kế tiếp mở theo độ dài mới, không sớm hơn lúc ván trước trả thưởng xong.</p>
          <InlineError error={stale ? null : m.error} />
        </div>
      </Modal>
    </div>
  );
}
